import React, { useEffect, useState } from "react";
import { getDepartments } from "../api/departmentService";
import { getProgramsByDepartment } from "../api/programService";
import { getSemestersByProgram } from "../api/semesterService";
import { getCoursesBySemester } from "../api/courseService";
import {
  getUnitsByCourse,
  createUnit,
  updateUnit,
  // deleteUnit,
} from "../api/courseUnitService";

import "../styles/AddUnit.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddUnit = () => {
  const [departments, setDepartments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [courses, setCourses] = useState([]);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState("");

  const [editingUnit, setEditingUnit] = useState(null);
  const [saving, setSaving] = useState(false);

  // Loader states for dropdowns
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingSemesters, setLoadingSemesters] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const [formData, setFormData] = useState({
    department_id: "",
    program_id: "",
    semester_id: "",
    course_id: "",
    name: "",
    code: "",
    description: "",
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const data = await getDepartments({ fields: "id,name" });
      setDepartments(data);
    } catch {
      toast.error("Failed to load departments");
    }
  };

  const loadPrograms = async (departmentId) => {
    setLoadingPrograms(true);
    setPrograms([]);
    setSemesters([]);
    setCourses([]);
    setUnits([]);
    setEditingUnit(null);
    try {
      const data = await getProgramsByDepartment(departmentId);
      setPrograms(data);
    } catch (err) {
      console.error("Failed to load programs:", err);
      toast.error("Failed to load programs");
    } finally {
      setLoadingPrograms(false);
    }
  };

  const loadSemesters = async (programId) => {
    setLoadingSemesters(true);
    setSemesters([]);
    setCourses([]);
    setUnits([]);
    setEditingUnit(null);
    try {
      const data = await getSemestersByProgram(programId);
      setSemesters(data);
    } catch (err) {
      console.error("Failed to load semesters:", err);
      toast.error("Failed to load semesters");
    } finally {
      setLoadingSemesters(false);
    }
  };

  const loadCourses = async (semesterId) => {
    setLoadingCourses(true);
    setCourses([]);
    setUnits([]);
    setEditingUnit(null);
    try {
      const data = await getCoursesBySemester(semesterId);
      setCourses(data);
    } catch (err) {
      console.error("Failed to load courses:", err);
      toast.error("Failed to load courses");
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadUnits = async (courseId) => {
    if (!courseId) return;
    setUnits([]);
    setEditingUnit(null);
    try {
      const data = await getUnitsByCourse(courseId);
      setUnits(data);
    } catch (err) {
      console.error("Failed to load units:", err);
      toast.error("Failed to load units");
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveUnit = async () => {
    if (!formData.course_id || !formData.name) {
      toast.warning("Please select a course and enter a unit name");
      return;
    }

    const payload = {
      name: formData.name,
      code: formData.code || formData.name.slice(0, 3).toUpperCase(),
      description: formData.description || "",
      course_id: parseInt(formData.course_id, 10),
    };

    setSaving(true);
    try {
      if (editingUnit) {
        await updateUnit(editingUnit.id, payload);
        toast.success("✅ Unit updated successfully!");
        setEditingUnit(null);
      } else {
        await createUnit(payload);
        toast.success("✅ Unit created successfully!");
      }

      setFormData((prev) => ({ ...prev, name: "", code: "", description: "" }));
      loadUnits(payload.course_id);
    } catch (err) {
      console.error("Failed to save unit:", err);
      toast.error("❌ Failed to save unit");
    } finally {
      setSaving(false);
    }
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    setFormData((prev) => ({
      ...prev,
      name: unit.name || "",
      code: unit.code || "",
      description: unit.description || "",
      course_id: unit.course_id?.[0] || prev.course_id,
    }));
  };

  return (
    <div className="add-unit-container">
      {/* Header */}
      <div className="section-card">
        <h2>📘 Unit Management</h2>

        <div className="form-grid">
          {/* Row 1: Department + Program */}
          <div className="row-group">
            <select
              value={formData.department_id}
              onChange={(e) => {
                const id = e.target.value;
                handleChange("department_id", id);
                handleChange("program_id", "");
                handleChange("semester_id", "");
                handleChange("course_id", "");
                handleChange("name", "");
                handleChange("code", "");
                handleChange("description", "");
                loadPrograms(id);
              }}
            >
              <option value="">Select Department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>

            <select
              value={formData.program_id}
              disabled={loadingPrograms || !programs.length}
              onChange={(e) => {
                const id = e.target.value;
                handleChange("program_id", id);
                handleChange("semester_id", "");
                handleChange("course_id", "");
                handleChange("name", "");
                handleChange("code", "");
                handleChange("description", "");
                loadSemesters(id);
              }}
            >
              {loadingPrograms ? (
                <option className="dropdown-loader">⏳ Loading programs...</option>
              ) : (
                <>
                  <option value="">Select Program</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* Row 2: Semester + Course */}
          <div className="row-group">
            <select
              value={formData.semester_id}
              disabled={loadingSemesters || !semesters.length}
              onChange={(e) => {
                const id = e.target.value;
                handleChange("semester_id", id);
                handleChange("course_id", "");
                handleChange("name", "");
                handleChange("code", "");
                handleChange("description", "");
                loadCourses(id);
              }}
            >
              {loadingSemesters ? (
                <option className="dropdown-loader">⏳ Loading semesters...</option>
              ) : (
                <>
                  <option value="">Select Semester</option>
                  {semesters.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </>
              )}
            </select>

            <select
              value={formData.course_id}
              disabled={loadingCourses || !courses.length}
              onChange={(e) => {
                const id = e.target.value;
                handleChange("course_id", id);
                handleChange("name", "");
                handleChange("code", "");
                handleChange("description", "");
                loadUnits(id);
              }}
            >
              {loadingCourses ? (
                <option className="dropdown-loader">⏳ Loading courses...</option>
              ) : (
                <>
                  <option value="">Select Course</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Editing Banner */}
      {editingUnit && (
        <div className="editing-banner">
          ✏️ Editing Unit: <strong>{editingUnit.name}</strong>
        </div>
      )}

      {/* Form Section */}
      <div className="section-card">
        <h3>{editingUnit ? "✏️ Edit Unit" : "➕ Create Unit"}</h3>

        <div className="unit-form">
          <input
            type="text"
            placeholder="Unit Name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          <input
            type="text"
            placeholder="Unit Code"
            value={formData.code}
            onChange={(e) => handleChange("code", e.target.value)}
          />
          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => {
              handleChange("description", e.target.value);
              const textarea = e.target;
              textarea.style.height = "auto";
              textarea.style.height = `${textarea.scrollHeight}px`;
            }}
            ref={(el) => {
              if (el) {
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
              }
            }}
            className="auto-textarea"
          />

          <button
            className="animated-save-btn"
            onClick={handleSaveUnit}
            disabled={saving || !formData.name || !formData.course_id}
          >
            {saving ? <span className="loader" /> : editingUnit ? "🔄 Update Unit" : "💾 Save Unit"}
          </button>
        </div>
      </div>

      {/* Units Table */}
      {units.length > 0 && (
        <div className="section-card">
          <div className="unit-header">
            <h3>📋 Units in Selected Course ({units.length})</h3>
            <input
              type="text"
              placeholder="🔍 Search Unit..."
              className="search-input"
              onChange={(e) => setSearch(e.target.value.toLowerCase())}
            />
          </div>

          <table className="programs-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Unit Name</th>
                <th>Code</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {units
                .filter((u) => u.name.toLowerCase().includes(search))
                .map((unit, idx) => (
                  <tr key={unit.id}>
                    <td>{idx + 1}</td>
                    <td>{unit.name}</td>
                    <td>{unit.code}</td>
                    <td>{unit.description || "—"}</td>
                    <td className="table-actions">
                      <button className="edit-btn" onClick={() => handleEditUnit(unit)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default AddUnit;
